import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  isSubmitting: boolean;
  screenshotUrl: string | null;
  success: boolean;
  error: string | null;
  onSubmit: (description: string) => void;
  onClose: () => void;
}

export function BugReportModal({
  isOpen,
  isSubmitting,
  screenshotUrl,
  success,
  error,
  onSubmit,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [description, setDescription] = useState("");
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isOpen && !dialog.open) {
      dialog.showModal();
    } else if (!isOpen && dialog.open) {
      dialog.close();
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;
    onSubmit(description.trim());
  };

  const handleClose = () => {
    setDescription("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      className="bg-transparent backdrop:bg-black/50 p-0 max-w-lg w-full"
    >
      <div className="bg-redi-surface rounded-2xl p-6 shadow-xl">
        {success ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">&#10003;</div>
            <h3 className="text-lg font-semibold text-redi-text">
              {t("bugReport.successTitle")}
            </h3>
            <p className="text-sm text-redi-muted mt-2">
              {t("bugReport.successMsg")}
            </p>
            <button
              onClick={handleClose}
              className="mt-6 px-6 py-2 bg-redi-primary text-white rounded-lg font-medium hover:bg-redi-primary/90 transition-colors"
            >
              OK
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-redi-text">
                {t("bugReport.title")}
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="text-redi-muted hover:text-redi-text transition-colors text-xl leading-none"
              >
                &times;
              </button>
            </div>

            {/* Description */}
            <label className="block text-sm text-redi-muted mb-2">
              {t("bugReport.descriptionLabel")}
            </label>
            <textarea
              className="w-full bg-redi-dark text-redi-text rounded-xl p-4 min-h-[120px] text-sm resize-none border border-transparent focus:border-redi-primary/50 focus:outline-none"
              placeholder={t("bugReport.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isSubmitting}
              autoFocus
            />

            {/* Screenshot */}
            {screenshotUrl && (
              <div className="mt-4">
                <p className="text-xs text-redi-muted mb-2">
                  {t("bugReport.screenshotIncluded")}
                </p>
                <img
                  src={screenshotUrl}
                  alt="Screenshot"
                  className="w-full h-32 object-cover rounded-xl border border-redi-dark"
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-red-400 mt-3">
                {t("bugReport.errorMsg")}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || !description.trim()}
              className="w-full mt-4 py-3 bg-redi-primary text-white rounded-xl font-medium hover:bg-redi-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t("bugReport.submitting") : t("bugReport.submit")}
            </button>
          </form>
        )}
      </div>
    </dialog>
  );
}
